import { useQuery } from '@tanstack/react-query';

import { fetchIntegrationConfig } from 'apis/integration-config-service/api';
import { IntegrationConfig, IntegrationType } from 'apis/integration-config-service/types';

import { getTypedKeys } from '../utils';

const CHUNK_SIZE = 100;

const MENU_INTEGRATED_TYPES: (keyof typeof IntegrationType)[] = [
  'SUBWAY',
  'MC_DONALD_GLOBAL_MENU',
  'MENU_API',
  'LISTINGS',
];

const isFullMenuIntegrated = (integrationConfigType: IntegrationType) =>
  MENU_INTEGRATED_TYPES.includes(integrationConfigType);

export const useIntegrationStatus = (venueId?: string) => {
  const { data, isInitialLoading } = useQuery(
    ['get-integration-configs', venueId],
    () => fetchIntegrationConfig(venueId ?? ''),
    {
      enabled: !!venueId,
    },
  );

  return {
    isLoadingIntegrationStatus: isInitialLoading,
    isFullMenuIntegrated: !!getTypedKeys(data?.enabledIntegrationTypes ?? {})?.some(type => isFullMenuIntegrated(type)),
  };
};

const chunkArray = (array: string[], chunkSize: number) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const batchIntegrationCalls = async (ids: string[]) => await Promise.all(ids.map(id => fetchIntegrationConfig(id)));

const fetchIntegrationConfigInChunks = async (ids: string[], chunkSize: number) => {
  const chunks = chunkArray(ids, chunkSize);
  const results = [];
  for (const chunk of chunks) {
    const chunkResults = await batchIntegrationCalls(chunk);
    results.push(...chunkResults);
  }
  return results;
};

export const useFetchIntegrationStatusBatch = (venueIds: string[], chunkSize: number = CHUNK_SIZE) =>
  useQuery(['get-integration-configs', venueIds], () => fetchIntegrationConfigInChunks(venueIds, chunkSize), {
    enabled: !!venueIds.length,
    select: (integrationConfigs: IntegrationConfig[]) => {
      const fullMenuIntegratedVenuesConfigs = integrationConfigs.filter(
        ic => !!getTypedKeys(ic.enabledIntegrationTypes ?? {})?.some(type => isFullMenuIntegrated(type)),
      );

      const fullMenuIntegratedVenuesIdsSet = new Set(fullMenuIntegratedVenuesConfigs.map(ic => ic.venueId));

      return { fullMenuIntegratedVenuesIdsSet };
    },
  });
